import { db } from '../firebase';
import { collection, query, where, getDocs, updateDoc, doc, increment } from 'firebase/firestore';

/**
 * Validates a coupon code and returns coupon details if valid
 * @param {string} couponCode - The coupon code to validate
 * @param {number} orderTotal - The order total to validate against minimum order amount
 * @returns {Object} - { isValid: boolean, coupon?: Object, error?: string }
 */
export const validateCoupon = async (couponCode, orderTotal = 0) => {
  try {
    if (!couponCode || !couponCode.trim()) {
      return { isValid: false, error: 'Please enter a coupon code' };
    }

    // Query for the coupon
    const couponsQuery = query(
      collection(db, 'coupons'),
      where('code', '==', couponCode.toUpperCase()),
      where('isActive', '==', true)
    );

    const snapshot = await getDocs(couponsQuery);

    if (snapshot.empty) {
      return { isValid: false, error: 'Invalid coupon code' };
    }

    const couponDoc = snapshot.docs[0];
    const coupon = { id: couponDoc.id, ...couponDoc.data() };

    // Check if coupon is expired
    const now = new Date();
    const expiryDate = new Date(coupon.expiryDate);
    if (expiryDate <= now) {
      return { isValid: false, error: 'This coupon has expired' };
    }

    // Check usage limit
    if (coupon.maxUses && coupon.usedCount >= coupon.maxUses) {
      return { isValid: false, error: 'This coupon has reached its usage limit' };
    }

    // Check minimum order amount
    if (coupon.minOrderAmount && orderTotal < coupon.minOrderAmount) {
      return { 
        isValid: false, 
        error: `Minimum order amount of $${coupon.minOrderAmount} required for this coupon` 
      };
    }

    return { isValid: true, coupon };
  } catch (error) {
    console.error('Error validating coupon:', error);
    return { isValid: false, error: 'Failed to validate coupon. Please try again.' };
  }
};

/**
 * Calculates the discount amount based on coupon type and value
 * @param {Object} coupon - The coupon object
 * @param {number} orderTotal - The order total
 * @returns {number} - The discount amount
 */
export const calculateDiscount = (coupon, orderTotal) => {
  if (!coupon || orderTotal <= 0) return 0;

  switch (coupon.type) {
    case 'percentage':
      return Math.min((orderTotal * coupon.value) / 100, orderTotal);
    case 'fixed':
      return Math.min(coupon.value, orderTotal);
    case 'free':
      return orderTotal; // 100% off
    default:
      return 0;
  }
};

/**
 * Applies a coupon to an order (increments usage count)
 * @param {string} couponId - The coupon document ID
 * @returns {Promise<boolean>} - Success status
 */
export const applyCoupon = async (couponId) => {
  try {
    const couponRef = doc(db, 'coupons', couponId);
    await updateDoc(couponRef, {
      usedCount: increment(1)
    });
    return true;
  } catch (error) {
    console.error('Error applying coupon:', error);
    return false;
  }
};

/**
 * Formats coupon value for display
 * @param {Object} coupon - The coupon object
 * @returns {string} - Formatted coupon value
 */
export const formatCouponValue = (coupon) => {
  if (!coupon) return '';
  
  switch (coupon.type) {
    case 'percentage':
      return `${coupon.value}% OFF`;
    case 'fixed':
      return `$${coupon.value} OFF`;
    case 'free':
      return 'FREE (100% OFF)';
    default:
      return '';
  }
};

/**
 * Gets all active testing coupons
 * @returns {Promise<Array>} - Array of testing coupons
 */
export const getTestingCoupons = async () => {
  try {
    const couponsQuery = query(
      collection(db, 'coupons'),
      where('isTesting', '==', true),
      where('isActive', '==', true)
    );

    const snapshot = await getDocs(couponsQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching testing coupons:', error);
    return [];
  }
}; 